import { useState } from "react"
import { motion } from "framer-motion"
import { 
  ArrowRight, Check
} from "lucide-react"
import { useNavigate } from "react-router-dom"
import IconWrapper from "../components/IconWrapper"

// Custom SVG Icon component with enhanced styling
const SvgIcon = ({ src, alt, className = '' }) => (
  <img 
    src={src} 
    alt={alt} 
    className={`w-8 h-8 filter brightness-0 invert ${className}`}
    style={{ 
      filter: 'brightness(0) invert(1) drop-shadow(0 0 8px rgba(0, 255, 255, 0.6))'
    }}
  />
)

// Tech stack item component
const TechItem = ({ name, logo, filter = true, useTextFallback = false, size = "normal" }) => (
  <motion.div
    whileHover={{ scale: 1.05 }}
    className="flex items-center justify-between bg-gradient-to-br from-purple-900/30 to-indigo-900/30 
               rounded-xl backdrop-blur-sm border border-purple-500/20 
               hover:border-purple-500/40 hover:shadow-lg hover:shadow-purple-500/10 
               transition-all duration-300 p-5 h-20"
  >
    <span className="text-white font-medium text-lg">{name}</span>
    <div className="w-12 h-12 flex items-center justify-center">
      {useTextFallback ? (
        <div className="w-10 h-10 rounded-full bg-purple-500/50 flex items-center justify-center text-white font-bold">
          {name.charAt(0)}
        </div>
      ) : name === "MistralAI" ? (
        <img 
          src={logo} 
          alt={name} 
          className="w-10 h-10 md:w-12 md:h-12 object-contain" 
        />
      ) : name === "Vercel" ? (
        <img 
          src={logo} 
          alt={name} 
          className="w-10 h-10 md:w-12 md:h-12 object-contain" 
        />
      ) : (
        <img 
          src={logo} 
          alt={name} 
          className={`max-h-10 max-w-10 object-contain ${filter ? 'filter invert-[0.15] brightness-150' : ''}`} 
        />
      )}
    </div>
  </motion.div>
)

const Home = () => {
  const [selectedTab, setSelectedTab] = useState("DEVELOP MY IDEA")
  const navigate = useNavigate()
  // Message popup state is kept for potential future use

  const servicesTabs = {
    "DEVELOP MY IDEA": {
      heading: "Let's turn your vision into a viable product.",
      services: [
        { 
          icon: (props) => <SvgIcon src="/web&Mobile.svg" alt="Web & Mobile" />, 
          text: "Web & Mobile App Development" 
        },
        { 
          icon: (props) => <SvgIcon src="/MVP.svg" alt="MVP Strategy" />, 
          text: "Minimum Viable Product (MVP) Strategy" 
        },
        { 
          icon: (props) => <SvgIcon src="/Riskiest.svg" alt="Riskiest Assumption Test" />, 
          text: "Riskiest Assumption Test" 
        },
        { 
          icon: (props) => <SvgIcon src="/Tech.svg" alt="Tech & Innovation" />, 
          text: "Tech & Innovation Consultancy" 
        },
        { 
          icon: (props) => <SvgIcon src="/Cross-Platform.svg" alt="Cross-Platform" />, 
          text: "Cross-Platform Solutions" 
        },
      ]
    },
    "OPTIMIZE MY BUSINESS": {
      heading: "Transform your enterprise's performance using smarter technology and leaner processes.",
      services: [
        { 
          icon: (props) => <SvgIcon src="/Ui_Ux.svg" alt="UI/UX Design" />, 
          text: "UI/UX Design Improvements" 
        },
        { 
          icon: (props) => <SvgIcon src="/Busines_Analysis.svg" alt="Business Analysis" />, 
          text: "Business Analysis & Strategy Formulation" 
        },
        { 
          icon: (props) => <SvgIcon src="/Quality_Assurance.svg" alt="Quality Assurance" />, 
          text: "Quality Assurance & Testing" 
        },
        { 
          icon: (props) => <SvgIcon src="/Maintenance.svg" alt="Maintenance & Support" />, 
          text: "Maintenance & Support Services" 
        },
        { 
          icon: (props) => <SvgIcon src="/Progressive_web.svg" alt="Progressive Web App" />, 
          text: "Progressive Web App Development" 
        },
      ]
    },
    "SCALE MY PROJECT": {
      heading: "Take your projects to the next level with our scalable solutions.",
      services: [
        { 
          icon: (props) => <SvgIcon src="/Cloud.svg" alt="Cloud Application" />, 
          text: "Cloud Application Development" 
        },
        { 
          icon: (props) => <SvgIcon src="/lowcode.svg" alt="Low-Code/No-Code" />, 
          text: "Low-Code/No-Code Solutions" 
        },
        { 
          icon: (props) => <SvgIcon src="/ai_assistants.svg" alt="AI Chatbots" />, 
          text: "AI-Powered Chatbots & AI Voice Assistants",
          onClick: () => navigate('/ai-voice-assistants')
        },
        { 
          icon: (props) => <SvgIcon src="/ai.svg" alt="AI & Machine Learning" />, 
          text: "AI & Machine Learning Implementation" 
        },
        { 
          icon: (props) => <SvgIcon src="/team.svg" alt="Dedicated Team" />, 
          text: "Dedicated Development Team" 
        },
      ]
    }
  }

  const benefits = [
    {
      text: "Industry-Specific Services",
      description: "Developing tailor-made solutions that offer the quality and user experience needed to appeal to your specific industry or sector."
    },
    {
      text: "Client-Centric Approach",
      description: "Our team offers the communication and soft skills necessary to help you bring your idea to life, exactly the way you envision it."
    },
    {
      text: "Quality Assured by our Strike Force Approach",
      description: "You'll be backed by a team of our best software developers, Strike Force, with the expertise needed to drive even the most difficult projects to a successful launch."
    },
    {
      text: "2-Week Trial Period",
      description: "We, as a web development company, offer a 2-week trial period."
    }
  ]

  // Tech stack data
  const techStack = {
    webDevelopment: [
      { name: "JavaScript", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/javascript/javascript-original.svg" },
      { name: "Node.js", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg" },
      { name: "Next.js", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nextjs/nextjs-original.svg" },
      { name: "React", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg" },
      { name: "TypeScript", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/typescript/typescript-original.svg" },
      { name: "Vue.js", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/vuejs/vuejs-original.svg" },
      { name: "Express.js", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/express/express-original.svg" },
      { 
        name: "Vercel", 
        logo: "/vercel-icon-dark.svg",
        filter: false,
        size: "large"
      },
    ],
    mobileDevelopment: [
      { name: "React Native", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg" },
    ],
    databasesAndApi: [
      { name: "MongoDB", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mongodb/mongodb-original.svg" },
      { name: "MySQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mysql/mysql-original.svg" },
      { 
        name: "REST API", 
        logo: "https://upload.wikimedia.org/wikipedia/commons/a/ab/Swagger-logo.png",
        filter: false 
      },
      { name: "GraphQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/graphql/graphql-plain.svg" },
      { 
        name: "Stripe", 
        logo: "https://upload.wikimedia.org/wikipedia/commons/b/ba/Stripe_Logo%2C_revised_2016.svg",
        filter: false 
      },
      { name: "Supabase", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/supabase/supabase-original.svg" },
      { 
        name: "Make.com", 
        logo: "/Make-Icon-Circle-Purple.svg",
        filter: false 
      },
      { 
        name: "OpenAI", 
        logo: "https://upload.wikimedia.org/wikipedia/commons/4/4d/OpenAI_Logo.svg",
        filter: false 
      },
      { 
        name: "MistralAI", 
        logo: "/Mistral_AI.svg",
        filter: false,
        size: "large"
      },
      { 
        name: "Claude by Anthropic", 
        logo: "/claude-icon.svg",
        filter: false
      },
      { 
        name: "Twilio", 
        logo: "/twilio-icon.svg",
        filter: false
      },
      { 
        name: "Retell AI", 
        logo: "/Retell-black.svg",
        filter: false,
        size: "large"
      },
    ],
  }

  return (
    <div className="relative overflow-hidden min-h-screen">
      {/* Background Elements - Fixed for mobile */}
      <div className="fixed inset-0 z-0">
        {/* Video Background */}
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
          poster="/worldbackground.png"
        >
          <source src="/background.mp4" type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        
        {/* Fallback background image for when video doesn't load */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: "url('/worldbackground.png')",
            backgroundSize: 'cover',
            backgroundPosition: 'center center',
            zIndex: -1
          }}
        />
        
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/80 via-purple-900/70 to-indigo-900/80" />
      </div>

      <div className="relative z-10 container mx-auto px-4">
        <div className="min-h-screen flex flex-col justify-center pb-16 md:pb-24">
          {/* Hero Section */}
          <section className="text-center max-w-5xl mx-auto">
            {/* Hero Section */}
            <div className="relative flex flex-col items-center">
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-white leading-tight"
              >
                Transform Your Business with AI Solutions
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="text-lg md:text-xl text-gray-300 mb-12"
              >
                Share your requirements & we'll create the perfect AI solution for your project
              </motion.p>

              {/* Original Get Started Button */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="mb-12"
              >
                <button
                  onClick={() => window.open("https://cal.com/tugadot/quickconnect", "_blank")}
                  className="group relative inline-flex items-center gap-3 px-6 md:px-8 py-3 md:py-4 
                               border-2 border-purple-500/50 hover:border-purple-400
                               rounded-full text-white font-semibold text-base md:text-lg 
                               transition-all duration-300 
                               hover:scale-105
                               hover:shadow-lg hover:shadow-purple-500/20"
                >
                  <span className="relative z-10 flex items-center gap-2">
                    Get Started
                    <ArrowRight className="w-4 h-4 md:w-5 md:h-5 group-hover:translate-x-1 transition-transform" />
                  </span>
                  <div className="absolute -top-2 md:-top-3 -right-2 md:-right-3">
                    <div
                      className="px-2 md:px-3 py-1 bg-gradient-to-r from-purple-500 to-purple-600 
                                text-xs font-bold rounded-full shadow-lg 
                                border border-purple-400/30 text-white/90
                                backdrop-blur-sm"
                    >
                      FREE
                    </div>
                  </div>
                </button>
              </motion.div>
            </div>
          </section>

          {/* Services Section */}
          <section id="services" className="mt-12 md:mt-20 mb-8 md:mb-12">
            <motion.div 
              initial={{ opacity: 0 }} 
              animate={{ opacity: 1 }} 
              transition={{ duration: 0.5, delay: 0.5 }}
            >
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-6 md:mb-8 text-center">
                Which software service best fits your needs?
              </h2>

              {/* Tabs - Sleek mobile design */}
              <div className="mb-6 md:mb-8">
                {/* Mobile segmented control */}
                <div className="sm:hidden bg-white/5 backdrop-blur-sm rounded-lg p-1 border border-white/10">
                  <div className="flex flex-col gap-1">
                    {Object.keys(servicesTabs).map((tab) => (
                      <motion.button
                        key={tab}
                        onClick={() => setSelectedTab(tab)}
                        whileTap={{ scale: 0.98 }}
                        className={`px-3 py-2 text-xs font-medium rounded-md transition-all duration-300 ${
                          selectedTab === tab 
                            ? "bg-purple-500/20 text-purple-300 border border-purple-500/30" 
                            : "text-gray-400 hover:text-gray-300 hover:bg-white/5"
                        }`}
                      >
                        {tab}
                      </motion.button>
                    ))}
                  </div>
                </div>
                
                {/* Desktop tabs */}
                <div className="hidden sm:flex justify-center border-b border-gray-700/50">
                  {Object.keys(servicesTabs).map((tab) => (
                    <motion.button
                      key={tab}
                      onClick={() => setSelectedTab(tab)}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className={`px-6 py-4 text-lg transition-all duration-300 border-b-2 font-medium ${
                        selectedTab === tab 
                          ? "text-white border-purple-500 font-semibold" 
                          : "text-gray-400 border-transparent hover:text-gray-300"
                      }`}
                    >
                      {tab}
                    </motion.button>
                  ))}
                </div>
              </div>

              {/* Content */}
              <motion.div
                key={selectedTab}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="bg-white/5 backdrop-blur-sm rounded-xl md:rounded-2xl p-4 md:p-8 border border-purple-500/20">
                  <div className="flex flex-col lg:flex-row gap-6 md:gap-8">
                    {/* Left Column - Services */}
                    <div className="lg:w-1/2">
                      <h3 className="text-lg md:text-xl font-semibold text-white mb-4 md:mb-6">Services we offer</h3>
                      <div className="space-y-3 md:space-y-4">
                        {servicesTabs[selectedTab].services.map((service, index) => (
                          <motion.div
                            key={service.text}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.1 }}
                            className="flex items-center bg-gradient-to-br from-purple-900/50 to-indigo-900/50 
                                     rounded-lg md:rounded-xl backdrop-blur-sm border border-purple-500/20 cursor-pointer 
                                     hover:border-purple-500/40 hover:shadow-lg hover:shadow-purple-500/10 
                                     transition-all duration-300 overflow-hidden group min-h-[60px] md:min-h-[70px]"
                            onClick={service.onClick}
                          >
                            {/* Mobile-optimized icon container */}
                            <div className="flex-shrink-0 p-3 md:p-4 bg-purple-900/50 border-r border-purple-500/20 flex items-center justify-center">
                              <IconWrapper
                                variant="neon"
                                size={24}
                                containerStyle={false}
                                className="mobile-icon-enhance"
                              >
                                <service.icon />
                              </IconWrapper>
                            </div>
                            <span className="text-white px-3 md:px-4 py-3 font-medium text-sm md:text-base flex-1">{service.text}</span>
                            <div className="flex-shrink-0 mr-3 md:mr-4">
                              <IconWrapper
                                variant="gradient"
                                size={16}
                                className="opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <ArrowRight />
                              </IconWrapper>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </div>

                    {/* Right Column - Benefits */}
                    <div className="lg:w-1/2">
                      <h3 className="text-lg md:text-xl font-semibold text-white mb-4 md:mb-6">{servicesTabs[selectedTab].heading}</h3>
                      <div className="space-y-4 md:space-y-6">
                        {benefits.map((benefit, index) => (
                          <motion.div
                            key={index}
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.1 }}
                            className="flex gap-3 md:gap-4"
                          >
                            <div className="flex-shrink-0 mt-1">
                              <IconWrapper
                                variant="neon"
                                size={16}
                                className="mobile-icon-enhance"
                              >
                                <Check />
                              </IconWrapper>
                            </div>
                            <div>
                              <h4 className="text-base md:text-lg font-medium text-white mb-1">{benefit.text}</h4>
                              <p className="text-gray-300 text-xs md:text-sm leading-relaxed">{benefit.description}</p>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                      
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5 }}
                        className="mt-6 md:mt-8"
                      >
                        <h3 className="text-lg md:text-xl font-semibold text-white mb-3 md:mb-4">
                          {selectedTab === "DEVELOP MY IDEA" ? "You dream it, we engineer it." :
                           selectedTab === "OPTIMIZE MY BUSINESS" ? "Unlock the full potential of your business." :
                           "Multiply success by implementing robust & scalable technology"}
                        </h3>
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-start gap-3 md:gap-4 mt-4 md:mt-6">
                          <button
                            onClick={() => window.open("https://cal.com/tugadot/quickconnect", "_blank")}
                            className="w-full sm:w-auto px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-purple-600 to-indigo-600 
                                     text-white font-semibold rounded-lg hover:from-purple-500 
                                     hover:to-indigo-500 transition-all duration-300 shadow-lg shadow-purple-600/20 text-sm md:text-base"
                          >
                            BOOK A FREE CONSULTATION
                          </button>
                          <button
                            className="w-full sm:w-auto px-4 md:px-6 py-2 md:py-3 border-2 border-purple-500/50 text-white 
                                     font-semibold rounded-lg hover:bg-purple-500/10 transition-all duration-300 text-sm md:text-base"
                          >
                            {selectedTab === "DEVELOP MY IDEA" ? "CHECK STEP-BY-STEP GUIDE" :
                             selectedTab === "OPTIMIZE MY BUSINESS" ? "VIEW CASE STUDIES" :
                             "CHECK PROJECT'S ROADMAP"}
                          </button>
                        </div>
                      </motion.div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </section>

          {/* Tech Stack Section removed to avoid duplication */}
        </div>
      </div>
    </div>
  )
}

export default Home

